import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatCPF, onlyDigits } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { logger } from "@/lib/logger";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Link2, Loader2, UserCheck, Shield } from "lucide-react";
import { toast } from "sonner";
import Header from "@/components/Header";

export default function VincularCpfPage() {
  const nav = useNavigate();
  const { user, setUser } = useAuth();
  const [cpf, setCpf] = useState("");
  const [codigo, setCodigo] = useState("");
  const [etapa, setEtapa] = useState("cpf"); // "cpf" ou "otp"
  const [emailMasked, setEmailMasked] = useState("");
  const [saving, setSaving] = useState(false);
  const [erro, setErro] = useState("");

  if (!user) {
    nav("/login", { replace: true });
    return null;
  }
  if (user.role !== "cidadao") {
    nav("/", { replace: true });
    return null;
  }
  if ((user.cpf || "").trim()) {
    // Já vinculado — vai direto pra área
    nav("/area-cidadao", { replace: true });
    return null;
  }

  const vincular = async (e) => {
    e.preventDefault();
    setErro("");
    const digits = onlyDigits(cpf);
    if (digits.length !== 11) {
      setErro("Informe um CPF válido com 11 dígitos");
      return;
    }
    setSaving(true);
    try {
      const payload = { cpf: digits };
      if (etapa === "otp" && codigo.trim()) payload.codigo = codigo.trim();
      const r = await api.put("/auth/google/link-cpf", payload);
      // Se o servidor pediu verificação, mostra o passo OTP
      if (r.data?.needs_verification) {
        setEmailMasked(r.data.email_masked || "");
        setEtapa("otp");
        toast.info(r.data.message || "Enviamos um código para o e-mail cadastrado.");
        return;
      }
      // Sucesso — atualiza user e redireciona
      setUser({ ...user, cpf: digits });
      const vinc = r.data.inscricoes_vinculadas || 0;
      if (vinc > 0) {
        toast.success(`CPF vinculado! Encontramos ${vinc} inscrição(ões) sua(s).`);
      } else {
        toast.success("CPF vinculado! Faça sua pré-inscrição a seguir.");
      }
      nav(vinc > 0 ? "/area-cidadao" : "/inscricao", { replace: true });
    } catch (err) {
      logger.error("Falha ao vincular CPF", err);
      setErro(err.response?.data?.detail || "Falha ao vincular CPF");
    } finally { setSaving(false); }
  };

  return (
    <div className="min-h-screen bg-[#F4F6F9]">
      <Header />
      <div className="max-w-lg mx-auto px-4 py-12" data-testid="vincular-cpf-page">
        <Card>
          <CardHeader>
            <div className="w-12 h-12 rounded-full bg-[#083CA6]/10 flex items-center justify-center mb-3">
              <Link2 className="w-6 h-6 text-[#083CA6]" />
            </div>
            <CardTitle className="text-2xl">Vincule seu CPF</CardTitle>
            <CardDescription>
              Olá, <strong>{user.nome}</strong> ({user.email}). Para acompanhar suas inscrições,
              informe o CPF que você usa (ou vai usar) no programa Minha Casa Minha Vida.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Alert className="mb-4 border-blue-200 bg-blue-50">
              <Shield className="w-4 h-4" />
              <AlertDescription className="text-xs">
                Se você já se inscreveu antes com este CPF, suas inscrições serão automaticamente
                vinculadas à sua conta Google.
              </AlertDescription>
            </Alert>
            <form onSubmit={vincular} className="space-y-4">
              <div>
                <Label htmlFor="cpf-input">CPF</Label>
                <Input
                  id="cpf-input"
                  value={cpf}
                  onChange={(e) => setCpf(formatCPF(e.target.value))}
                  placeholder="000.000.000-00"
                  autoFocus={etapa === "cpf"}
                  required
                  disabled={etapa === "otp"}
                  data-testid="input-link-cpf"
                />
              </div>
              {etapa === "otp" && (
                <div>
                  <Label htmlFor="otp-input">Código de verificação</Label>
                  <Input
                    id="otp-input"
                    value={codigo}
                    onChange={(e) => setCodigo(e.target.value.replace(/\D/g, "").slice(0, 6))}
                    placeholder="000000"
                    maxLength={6}
                    autoFocus
                    required
                    className="text-center text-2xl tracking-widest font-mono"
                    data-testid="input-link-cpf-otp"
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    Código enviado para <strong>{emailMasked}</strong>. Expira em 15 minutos.
                  </p>
                </div>
              )}
              {erro && (
                <Alert className="border-red-300 bg-red-50">
                  <AlertDescription className="text-sm text-red-800" data-testid="link-cpf-error">
                    {erro}
                  </AlertDescription>
                </Alert>
              )}
              <Button
                type="submit"
                disabled={saving}
                className="w-full bg-[#083CA6] hover:bg-[#062f85]"
                data-testid="btn-vincular-cpf"
              >
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <UserCheck className="w-4 h-4 mr-2" />}
                {etapa === "otp" ? "Confirmar código e vincular" : "Vincular CPF"}
              </Button>
              {etapa === "otp" && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="w-full text-xs"
                  onClick={() => { setEtapa("cpf"); setCodigo(""); setErro(""); }}
                >
                  ← Trocar CPF
                </Button>
              )}
            </form>
            <p className="text-xs text-slate-500 mt-4 text-center">
              Problemas? Entre em contato com a Secretaria de Habitação de Grajaú.
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
